// Pure helper functions for the transport logic.
// loadAllInstances and parseInstanceWerke are used by the transport-v2 flow
// (see hooks/useSpaceLoader.js); the rest of this module is currently unused.
// Important: no React import. Pure functions only (avoids a TDZ trap).

export const PIPELINE_ROLE_ORDER = {
  script: 0,
  generic: 1,
  rai: 2,
  mapping: 3,
  other: 4,
  normal: 5,
  sai: 6,
};

export function classifyPipelineRole(name) {
  if (/Pipeline v2 - Script Collection/i.test(name)) return "script";
  if (/Pipeline v2 (Generic Step|Template|Integrated Messaging Runtime)/i.test(name)) return "generic";
  if (/\bRAI\b|Receiver\s+Adapter\s+iFlow/i.test(name)) return "rai";
  if (/\bSAI\b|Sender\s+Adapter\s+iFlow/i.test(name)) return "sai";
  if (/[Mm]apping.*[Pp]ipeline|[Pp]ipeline.*[Mm]apping/i.test(name)) return "mapping";
  if (/[Pp]ipeline/i.test(name)) return "other";
  return "normal";
}

// Access policy compliance pre-flight gate
// api: injected api function (utils/helpers.js::api)
// confirmFn: injected confirm function (default window.confirm), kept injectable for tests
export async function runComplianceGate(targetId, pkg, artefakte, api, confirmFn) {
  const confirmer = confirmFn || (typeof window !== "undefined" ? window.confirm.bind(window) : () => true);
  if (!targetId || !pkg) return { ok: true, info: { status: "skipped" } };
  try {
    const r = await api("/api/transport/compliance-check", {
      method: "POST",
      body: JSON.stringify({
        targetInstanceId: targetId,
        targetPackage: { id: pkg.Id || pkg.id, name: pkg.Name || pkg.name },
        artefakte: (artefakte || []).map(a => ({
          id: a.Id || a.id,
          name: a.Name || a.name,
          type: a.type || "IntegrationFlow",
        })),
      }),
    });
    if (r.status === "ok" || r.status === "unavailable" || r.status === "disabled" || r.status === "skipped") {
      return { ok: true, info: r };
    }
    const lines = [];
    if (r.status === "no_match") lines.push("Compliance Prüfung: Das Paket passt zu keiner Access Policy am Ziel Tenant.");
    if (r.status === "ambiguous") lines.push("Compliance Prüfung: Das Paket passt zu mehreren Access Policies. Bitte den Paketnamen schärfer fassen.");
    if (r.status === "artefacts_outside") lines.push(`Compliance Prüfung: Das Paket passt zur Policy ${r.detectedPolicy?.roleName || ""}, einige Artefakte aber nicht.`);
    if (r.hint) lines.push(r.hint);
    if (Array.isArray(r.misfits) && r.misfits.length) {
      lines.push("Betroffene Artefakte: " + r.misfits.map(m => m.name).join(", "));
    }
    lines.push("");
    lines.push("Transport trotzdem durchführen? Hinweis: Wenn der Tenant Access Policies durchsetzt, wird der Transport am Tenant scheitern.");
    return { ok: confirmer(lines.join("\n")), info: r };
  } catch (e) {
    return { ok: true, info: { status: "error", error: e.message } };
  }
}

// Build the configurations payload from the config array.
// Pure: no mutation, no side effects.
export function buildConfigPayload(cfgs) {
  if (!cfgs) return undefined;
  const modified = cfgs.filter(c => {
    const effectiveValue = c.newValue || (c.group !== "sender" && c.group !== "receiver" ? c.defaultValue : "");
    return effectiveValue && effectiveValue !== c.defaultValue;
  }).map(c => ({
    ParameterKey: c.ParameterKey,
    ParameterValue: c.newValue || (c.group !== "sender" && c.group !== "receiver" ? c.defaultValue : ""),
    DataType: c.DataType,
  }));
  return modified.length ? modified : undefined;
}

// Cache key for artifact configs.
// Pure variant with explicitly injected values (no React state access).
export function getConfigCacheKey(artId, werkKey, activeWerk, targetId) {
  const werk = werkKey || activeWerk;
  if (werk) return `${artId}__${werk}`;
  return targetId ? `${artId}__${targetId}` : artId;
}

// Strips the kennung suffix from an id (e.g. iFlow_ABC_DE10 -> iFlow_ABC)
export function stripKennungSuffix(id, kennung) {
  if (!id || !kennung) return id;
  if (id.endsWith(kennung)) return id.slice(0, -kennung.length);
  return id;
}

// Loads every instance from every space, optionally including the source instance.
// api: injected api function
// options.excludeId: instance id to exclude (e.g. the source)
// options.includeAll: when true, includes all instances, even the source
export async function loadAllInstances(api, options = {}) {
  const { excludeId, includeAll } = options;
  try {
    const spaces = await api("/api/spaces");
    const insts = [];
    for (const sp of spaces) {
      try {
        const is = await api(`/api/spaces/${sp.id}/instances`);
        is.forEach(i => insts.push({ ...i, spaceName: sp.name }));
      } catch {}
    }
    if (includeAll) return insts;
    if (excludeId) return insts.filter(i => i.id !== excludeId);
    return insts;
  } catch {
    return [];
  }
}

// Extracts werke per instance. Supports three formats:
//   - object: {name:"Werk Stuttgart", kennung:"97"}
//   - string with separator: "Werk Stuttgart__97"
//   - plain string (legacy): "97" -> name and kennung both become "97"
export function parseInstanceWerke(instance) {
  if (!instance || !Array.isArray(instance.werke)) return [];
  return instance.werke.map(w => {
    if (typeof w === "string") {
      const parts = w.split("__");
      if (parts.length >= 2) return { name: parts[0], kennung: parts[1] };
      return { name: parts[0], kennung: parts[0] };
    }
    return { name: w.name || "", kennung: w.kennung || "" };
  }).filter(w => w.kennung);
}
